import { IntegrationSource } from '@/constants';
import { PaginatedResponse } from '@/types/api.types';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import { queryKeys } from './queryKeys';
import { useAppQuery } from './useAppQuery';

export interface IStudentCourse {
  /** Stable per-row identity. A retaken course returns one row per LearnUpon enrolment and they all
   *  share courseSourceId, so the SKU can no longer key a grid row. */
  rowKey: string;
  courseSourceId: string;
  courseId: string | null;
  courseName: string;
  status: string;
  dueDate: string | null;
  /** When the student's access to the course content ends; null when access never expires. */
  courseAccessExpiresAt: string | null;
  completedDate: string | null;
  isOverdue: boolean;
  percentageCompleted: number;
  learnUponEnrollmentId?: string;
  learnUponInstance?: number | null;
  /** Set on enrolments from the organisation's own LearnUpon portal; sent back on unenrol. */
  integrationId?: string | null;
  integrationSource: IntegrationSource;
  canSendReminder: boolean;
  enrollable: boolean;
  /** Internal CourseEnrollment id — only set on the local-cache (My Klayo 2.0) path. Used to
   *  fetch the per-enrolment learning history. */
  courseEnrollmentId?: string | null;
  canUnenrol: boolean;
  /** Internal EmployeeTraining id for manual-training rows. When set, the UI knows this is a
   *  manual training and uses the request activity feed in place of the enrolment history table. */
  employeeTrainingId?: string | null;
  /** LearningPathEnrollment id; sent to the history endpoint in place of courseEnrollmentId for
   *  learning-path enrolments. */
  learningPathEnrollmentId?: string | null;
  /** IntegrationLearnUponEnrollment id — only set on rows from the organisation's own LearnUpon
   *  portal. Used to fetch their learning history. */
  integrationEnrollmentId?: string | null;
  /** True when at least one issued certificate exists for this employee + course. Drives the
   *  "View Certificate" row action. */
  hasCertificate: boolean;
  /** Server-computed: live LearnUpon enrolment, has a courseEnrollmentId, and not yet
   *  terminal-pass. Gates the "Set due date"/"Set expiry date" row actions. */
  canSetDates: boolean;
}

export interface IStudentCoursesResponse {
  courses: PaginatedResponse<IStudentCourse>;
}

/** The four period filters, already resolved to concrete boundaries by the caller. Sent here as well
 *  as to the students list: they narrow the enrolments each student's counters are built from, so
 *  omitting them makes this drill-down disagree with the count that opened it. */
export interface IStudentCourseDateFilters {
  enrolmentDateFrom?: string;
  enrolmentDateTo?: string;
  completedDateFrom?: string;
  completedDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  expiryDateFrom?: string;
  expiryDateTo?: string;
}

interface Props extends IStudentCourseDateFilters {
  employeeId?: string;
  statusFilter?: string;
  paginationModel?: GridPaginationModel;
  providerId?: string;
  facultyIds?: string[];
  courseIds?: string[];
}

export const useGetStudentCourses = ({
  employeeId,
  statusFilter,
  paginationModel,
  providerId,
  facultyIds,
  courseIds,
  ...dates
}: Props) => {
  // Sort filter arrays so cache keys are stable regardless of selection order.
  const sortedFacultyIds = facultyIds?.length ? [...facultyIds].sort() : undefined;
  const sortedCourseIds = courseIds?.length ? [...courseIds].sort() : undefined;

  return useAppQuery<IStudentCoursesResponse>(`/TrainingManager/students/${employeeId}/courses`, {
    config: {
      params: {
        statusFilter: statusFilter || undefined,
        pageNumber: (paginationModel?.page || 0) + 1,
        pageSize: paginationModel?.pageSize || undefined,
        providerId: providerId || undefined,
        facultyIds: sortedFacultyIds,
        courseIds: sortedCourseIds,
        ...dates,
      },
    },
    enabled: !!employeeId,
    placeholderData: undefined,
    queryKey: queryKeys.studentCourses.list({
      employeeId,
      statusFilter,
      page: paginationModel?.page,
      pageSize: paginationModel?.pageSize,
      providerId,
      facultyIds: sortedFacultyIds,
      courseIds: sortedCourseIds,
      ...dates,
    }),
  });
};
